import { useState, useEffect } from "react";
import t from "./lib/translations.json";

import { LanguageContext } from "./components/languageContext";

export const languages = ["en", "zh-TW", "es", "tl"] as const;
export type Language = (typeof languages)[number];
export type TextId = keyof typeof t;

export default function Translations({
  children,
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language | undefined>();

  const urlParams = new URLSearchParams(window.location.search); // Parse URL parameters
  const urlParamsLang = urlParams.get("lang");

  /**
   * Parse the query parameter `lang` from the URL and set the `language` state.
   * If the `lang` parameter is not found, default to 'en'.
   * For Chinese languages, default to 'zh-TW' for consistency.
   */
  useEffect(() => {
    const lang: string = urlParamsLang || navigator.language || "en"; // Get the 'lang' param or default to 'en'

    // Set language based on the available translations or fallback logic for Chinese
    if (languages.includes(lang as Language)) {
      setLanguage(lang as Language);
    } else if (lang.startsWith("zh")) {
      setLanguage("zh-TW"); // Default to traditional Chinese if 'zh' is found
    } else {
      setLanguage("en"); // Fallback to English if no match
    }
  }, [urlParamsLang]);

  if (!language) return null;

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        translate: (textID: TextId) => {
          if (!t[textID]) {
            throw new Error(`Translation for key "${textID}" not found`);
          }
          return t[textID][language];
        },
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}
